import { inject, Injectable } from '@angular/core';
import { CookieService, type CookieOptions, type SameSite } from 'ngx-cookie-service';

export type { CookieOptions, SameSite } from 'ngx-cookie-service';

/**
 * Browser cookies via {@link CookieService} (ngx-cookie-service).
 * Use for tokens or prefs that should be sent with requests (when configured).
 */
@Injectable({ providedIn: 'root' })
export class AppCookieService {
  private readonly ngx = inject(CookieService);

  /** Read a cookie by name (empty string if missing). */
  getCookie(name: string): string {
    return this.ngx.get(name);
  }

  /** True if a non-empty cookie exists for {@param name}. */
  hasCookie(name: string): boolean {
    return this.ngx.check(name);
  }

  /**
   * Save / update a cookie.
   * Defaults to `path: '/'` so cookies are consistent regardless of the current URL.
   */
  saveCookie(name: string, value: string, options?: CookieOptions): void {
    const opts: CookieOptions = { path: '/', ...options };
    this.deleteCookieAllPaths(name);
    this.ngx.set(name, value, opts);
  }

  /** Remove a cookie. Defaults to `path: '/'` to match how cookies are saved. */
  removeCookie(
    name: string,
    path: CookieOptions['path'] = '/',
    domain?: CookieOptions['domain'],
    secure?: CookieOptions['secure'],
    sameSite?: SameSite,
  ): void {
    this.ngx.delete(name, path, domain, secure, sameSite);
    // Also remove any duplicate stored at the browser default-path (no explicit Path).
    this.ngx.delete(name, undefined, domain, secure, sameSite);
  }

  /** All cookies as a plain object. */
  getAllCookies(): Record<string, string> {
    return this.ngx.getAll();
  }

  /** Remove every cookie the service can clear for the given path/domain. Defaults to `path: '/'`. */
  removeAllCookies(
    path: CookieOptions['path'] = '/',
    domain?: CookieOptions['domain'],
    secure?: CookieOptions['secure'],
    sameSite?: SameSite,
  ): void {
    // Delete with explicit path
    this.ngx.deleteAll(path, domain, secure, sameSite);
    // Also clear duplicates at every path prefix of the current URL
    for (const p of this.getPathSegments()) {
      this.ngx.deleteAll(p, domain, secure, sameSite);
    }
  }

  /**
   * Delete a cookie by name across all path prefixes of the current URL.
   * Browsers treat same-name cookies with different Path values as distinct;
   * this ensures stale duplicates from older code paths are removed.
   */
  private deleteCookieAllPaths(name: string): void {
    this.ngx.delete(name, '/');
    this.ngx.delete(name);
    for (const p of this.getPathSegments()) {
      this.ngx.delete(name, p);
    }
  }

  /** Returns all path prefixes for the current URL, e.g. '/users/list' → ['/users', '/users/list']. */
  private getPathSegments(): string[] {
    const pathname = globalThis.location?.pathname ?? '/';
    const parts = pathname.split('/').filter((s) => s.length > 0);
    const paths: string[] = [];
    let current = '';
    for (const part of parts) {
      current += '/' + part;
      paths.push(current);
    }
    return paths;
  }
}
